import { IntlShape } from "react-intl";
import { CheerzApiCustomizationOption, SelectedUpsell } from "@Types/api/customization";
import { ProductOption } from "@Types/cart";
import { Upsell, UpsellIdentifiers } from "@Types/upsells";
import { convertPriceFromApiToContent } from "./cart/price";

const optionsNotUpsells = [
  "additional_packs",
  "additional_pages",
  "additionnal_pages",
  "additional_photos",
  "format",
  "paper_type",
  "polabox_box",
];
const productsWithoutUpsell = ["gift"];
export const specialOptionsTags: { [key: string]: string } = {
  "box-20x20": "box",
  "box-28x28": "box",
};

export const getUpsellsToDisplay = (productOptions: ProductOption[]): ProductOption[] =>
  productOptions.filter((option) => !new Set(optionsNotUpsells).has(option.productOptionTag));

export const hasProductUpsells = (tag: string): boolean => !productsWithoutUpsell.includes(tag);

export const getTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>
): number => {
  const price = upsells
    .map((upsell) => {
      return selectedUpsellsTags.find(
        (tags) => tags.tag === upsell.tag && tags.value === upsell.value
      )
        ? upsell.price.cents / 100
        : 0;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue);
  return price;
};

export const getFormattedTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>,
  intl: IntlShape
): string => {
  const currency = upsells[0].price.currency;
  const price = getTotalPrice(upsells, selectedUpsellsTags);
  const formattedPrice = intl.formatNumber(price, {
    currency,
    style: "currency",
  });
  return formattedPrice;
};

export const isSelected = (
  tag: string,
  value: string,
  selectedUpsellsTags: UpsellIdentifiers[]
): boolean => {
  return selectedUpsellsTags.some(
    (upsellTags) => upsellTags.tag === tag && upsellTags.value === value
  );
};

export const formatSelectedOptions = (
  selectedUpsells: UpsellIdentifiers[],
  productOptions: Upsell[] | undefined
): SelectedUpsell[] | undefined => {
  if (productOptions) {
    const productOptionsTags = productOptions.map((productOption) => productOption.tag);
    const productOptionsTagsHash = new Set(productOptionsTags);

    return selectedUpsells.reduce((results, option) => {
      if (productOptionsTagsHash.has(option.tag)) {
        return results.concat([{ quantity: 1, tag: option.tag, value: option.value }]);
      } else return results;
    }, [] as SelectedUpsell[]);
  }
};

export const convertUpsellsFromApiToContent = (
  productOptions: CheerzApiCustomizationOption[]
): Upsell[] => {
  const flattenedOptions = productOptions.reduce((reducedOptions, productOption) => {
    if (productOption.option_values.length > 1) {
      const includedOptions = productOption.option_values.map((optionValue) => {
        return {
          ...productOption,
          option_values: productOption.option_values.filter(
            (option) => option.tag === optionValue.tag
          ),
        };
      });
      return [...reducedOptions, ...includedOptions];
    } else {
      return [...reducedOptions, productOption];
    }
  }, [] as CheerzApiCustomizationOption[]);

  return flattenedOptions.reduce((upsells, curr) => {
    const values = curr.option_values.length > 0 && curr.option_values[0];
    if (
      !!values &&
      !!values.description &&
      !!values.image["1x"] &&
      !!values.image["2x"] &&
      !!values.image["3x"] &&
      !!values.title &&
      curr.type === "upsell"
    ) {
      const upsell: Upsell = {
        description: values.description,
        image: {
          "1x": values.image["1x"],
          "2x": values.image["2x"],
          "3x": values.image["3x"],
        },
        price: convertPriceFromApiToContent(values.price),
        tag: curr.tag,
        title: values.title,
        value: values.tag,
      };
      upsells.push(upsell);
    }
    return upsells;
  }, [] as Upsell[]);
};
